<script lang="ts">
  /**
   * MoveToProjectPanel - searchable project list for moving tasks between projects.
   *
   * Renders in place (no positioning of its own) so a task menu can swap its
   * items for this panel — the "drill-in" pattern — instead of stacking another
   * floating layer. MoveToProjectPicker wraps it when a free-floating picker is
   * still wanted (e.g. bulk moves).
   *
   * Fetches projects on demand from /projects/search.json, sorted by most recently
   * active, with debounced type-ahead search and ↑/↓/Enter keyboard selection.
   * Escape calls `onBack` (drill-in menus) or `onEscape` (standalone pickers).
   * Keys and clicks handled here don't propagate, so host menus and popovers
   * don't also react to them.
   */
  import { onMount, tick } from 'svelte';
  import { categoryIcons } from '../lib/utils';
  import { jsonHeaders } from '../lib/apiHelpers';
  import Icon from './Icon.svelte';

  interface ProjectOption {
    id: string;
    title: string;
    category: string;
    schedulingState: string;
    status: string;
    updatedAt: string;
    isPriority: boolean;
    monthKey: string | null;
  }

  interface SearchResponse {
    projects: ProjectOption[];
    total: number;
    limit: number;
  }

  interface Props {
    /** The project ID to exclude (the task's current project) */
    excludeProjectId: string;
    /** Called when a project is selected */
    onSelect: (projectId: string, projectTitle: string) => void;
    /** Shows a back button in the header; Escape also goes back. */
    onBack?: () => void;
    /** Escape handler when there is no back step (standalone picker). */
    onEscape?: () => void;
    /** Header text; pass null to hide the header entirely. */
    title?: string | null;
    placeholder?: string;
    /** Focus the search field on mount. Hosts on touch pass false so the keyboard doesn't cover the list. */
    autofocus?: boolean;
  }

  let {
    excludeProjectId,
    onSelect,
    onBack,
    onEscape,
    title = 'Move to project',
    placeholder = 'Search projects...',
    autofocus = true,
  }: Props = $props();

  let searchQuery = $state('');
  let projects = $state<ProjectOption[]>([]);
  let loading = $state(true);
  let error = $state(false);
  let selectedIndex = $state(0);
  let searchInputEl: HTMLInputElement | undefined = $state();
  let listEl: HTMLDivElement | undefined = $state();
  let fetchRequestId = 0;

  const SEARCH_DEBOUNCE_MS = 180;

  let filteredProjects = $derived.by(() => {
    if (!searchQuery.trim()) return projects;
    const q = searchQuery.toLowerCase();
    return projects.filter((p) => p.title.toLowerCase().includes(q));
  });

  // Fetch projects when the search query changes (debounced), and once on mount.
  // eslint-disable-next-line momentium/no-unguarded-effect -- debounced data fetch: triggers on query changes and on initial mount
  $effect(() => {
    const q = searchQuery.trim();
    const timer = setTimeout(() => fetchProjects(q), q ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
  });

  // Reset keyboard selection whenever the visible list changes.
  // eslint-disable-next-line momentium/no-unguarded-effect -- UI sync: reset keyboard selection when filtered list changes
  $effect(() => {
    void filteredProjects;
    selectedIndex = 0;
  });

  /**
   * Focus the search field. Called on mount for hosts that are already visible
   * (drill-in menus); hosts that start `visibility: hidden` until Floating UI
   * positions them call it again afterwards, since hidden elements can't take focus.
   */
  export function focus() {
    searchInputEl?.focus({ preventScroll: true });
  }

  onMount(() => {
    if (autofocus) tick().then(focus);
  });

  async function fetchProjects(query = '') {
    const requestId = ++fetchRequestId;
    loading = true;
    error = false;

    try {
      const encodedExclude = encodeURIComponent(excludeProjectId);
      const encodedQuery = query ? `&q=${encodeURIComponent(query)}` : '';

      const response = await fetch(
        `/projects/search.json?exclude=${encodedExclude}${encodedQuery}`,
        {
          headers: jsonHeaders(),
          // Dynamic, per-user results — don't let the browser revalidate a
          // cached copy (Rails' conditional GET can return 304, which Safari
          // surfaces to fetch() as a non-ok empty-body response).
          cache: 'no-store',
        },
      );
      // 304 Not Modified (cache revalidation): keep current results, not an error.
      if (response.status === 304) return;
      if (!response.ok) throw new Error('Failed to fetch');

      const payload: SearchResponse = await response.json();
      if (requestId === fetchRequestId) {
        projects = payload.projects;
      }
    } catch {
      if (requestId === fetchRequestId) {
        projects = [];
        error = true;
      }
    } finally {
      if (requestId === fetchRequestId) {
        loading = false;
      }
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (onBack) onBack();
      else onEscape?.();
    } else if (e.key === 'ArrowDown') {
      e.stopPropagation();
      if (filteredProjects.length === 0) return;
      e.preventDefault();
      selectedIndex = Math.min(selectedIndex + 1, filteredProjects.length - 1);
      scrollSelectedIntoView();
    } else if (e.key === 'ArrowUp') {
      e.stopPropagation();
      if (filteredProjects.length === 0) return;
      e.preventDefault();
      selectedIndex = Math.max(selectedIndex - 1, 0);
      scrollSelectedIntoView();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const project = filteredProjects[selectedIndex];
      if (project) onSelect(project.id, project.title);
    }
  }

  function scrollSelectedIntoView() {
    tick().then(() => {
      const item = listEl?.querySelector('.project-item.selected');
      item?.scrollIntoView?.({ block: 'nearest' });
    });
  }

  function getSchedulingLabel(project: ProjectOption): string {
    if (project.schedulingState === 'backlog') return 'backlog';
    return project.status === 'active'
      ? 'active'
      : project.status === 'almost'
        ? 'almost done'
        : 'scheduled';
  }
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="move-panel"
  onkeydown={handleKeydown}
  onclick={(e) => e.stopPropagation()}
  onmousedown={(e) => e.stopPropagation()}
>
  {#if title !== null}
    <div class="panel-header" class:has-back={!!onBack}>
      {#if onBack}
        <button type="button" class="back-btn" onclick={onBack} title="Back" aria-label="Back">
          <Icon name="chevron-left" size={14} />
        </button>
      {/if}
      <span class="panel-title">{title}</span>
    </div>
  {/if}

  <div class="search-wrapper">
    <Icon name="search" size={13} class="search-icon" />
    <input
      autocomplete="off"
      type="text"
      class="search-input"
      aria-label="Search projects"
      {placeholder}
      bind:this={searchInputEl}
      bind:value={searchQuery}
    />
  </div>

  <div class="project-list" bind:this={listEl} role="listbox" aria-label="Projects">
    {#if loading && projects.length === 0}
      <div class="picker-empty">Loading projects…</div>
    {:else if error}
      <div class="picker-empty picker-error">Failed to load projects</div>
    {:else if filteredProjects.length === 0}
      <div class="picker-empty">
        {searchQuery ? 'No matching projects' : 'No other projects'}
      </div>
    {:else}
      {#each filteredProjects as project, i (project.id)}
        <button
          type="button"
          class="project-item"
          class:selected={i === selectedIndex}
          role="option"
          aria-selected={i === selectedIndex}
          onmouseenter={() => (selectedIndex = i)}
          onclick={() => onSelect(project.id, project.title)}
        >
          <span class="project-icon">{categoryIcons[project.category] || '📋'}</span>
          <span class="project-title">{project.title}</span>
          <span class="project-meta">{getSchedulingLabel(project)}</span>
        </button>
      {/each}
    {/if}
  </div>
</div>

<style>
  .move-panel {
    display: flex;
    flex-direction: column;
    min-width: 0;
    font-family: inherit;
  }

  .panel-header {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0.5rem 0.75rem 0.25rem;
  }

  .panel-header.has-back {
    padding-left: 0.25rem;
  }

  .back-btn {
    width: 1.5rem;
    height: 1.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #6b7280;
    background: none;
    border: none;
    border-radius: 0.3125rem;
    cursor: pointer;
  }

  .back-btn:hover {
    color: #1f2937;
    background: #f3f4f6;
  }

  .back-btn:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }

  .panel-title {
    font-size: 0.75rem;
    font-weight: 600;
    color: #374151;
  }

  .search-wrapper {
    position: relative;
    padding: 0.375rem 0.5rem 0.5rem;
    border-bottom: 1px solid #f3f4f6;
  }

  .search-wrapper :global(.search-icon) {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    margin-top: -0.0625rem;
    color: #9ca3af;
    pointer-events: none;
  }

  .search-input {
    width: 100%;
    height: 1.875rem;
    padding: 0 0.5rem 0 1.75rem;
    font-size: 0.8125rem;
    font-family: inherit;
    color: #1f2937;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    outline: none;
    transition:
      border-color 0.15s,
      background-color 0.15s;
  }

  .search-input:focus {
    border-color: #93c5fd;
    background: white;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.12);
  }

  .search-input::placeholder {
    color: #9ca3af;
  }

  .project-list {
    max-height: 13rem;
    overflow-y: auto;
    padding: 0.25rem;
  }

  .picker-empty {
    padding: 1rem 0.75rem;
    font-size: 0.8125rem;
    color: #9ca3af;
    text-align: center;
  }

  .picker-error {
    color: #dc2626;
  }

  .project-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    min-height: 1.875rem;
    padding: 0.25rem 0.5rem;
    font-family: inherit;
    border: none;
    border-radius: 0.3125rem;
    background: transparent;
    cursor: pointer;
    text-align: left;
    transition: background-color 0.1s;
  }

  .project-item.selected {
    background: #f3f4f6;
  }

  .project-icon {
    font-size: 0.75rem;
    flex-shrink: 0;
    width: 1.25rem;
    text-align: center;
  }

  .project-title {
    flex: 1;
    min-width: 0;
    font-size: 0.8125rem;
    color: #1f2937;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .project-meta {
    font-size: 0.625rem;
    color: #9ca3af;
    flex-shrink: 0;
    white-space: nowrap;
  }

  /* Phones: touch-sized rows (TaskDetailPopover's bottom sheet, bulk-move picker) */
  @media (max-width: 767px) {
    .panel-header {
      padding: 0.25rem 1rem;
    }

    .panel-header.has-back {
      padding-left: 0.25rem;
    }

    .back-btn {
      width: var(--mobile-tap);
      height: var(--mobile-tap);
    }

    .panel-title {
      font-size: 0.9375rem;
    }

    .search-wrapper {
      padding: 0.25rem 1rem 0.625rem;
    }

    .search-wrapper :global(.search-icon) {
      left: 1.625rem;
    }

    .search-input {
      height: 2.75rem;
      padding-left: 2rem;
      border-radius: 0.625rem;
    }

    .project-list {
      max-height: 45dvh;
      padding: 0.25rem 0.5rem;
    }

    .project-item {
      min-height: 3rem;
      gap: 0.75rem;
      padding: 0.25rem 0.5rem;
      border-radius: 0.5rem;
    }

    .project-icon {
      font-size: 1rem;
    }

    .project-title {
      font-size: 0.9375rem;
    }

    .project-meta {
      font-size: 0.75rem;
    }
  }
</style>
